import React, { useMemo } from 'react';

export type WorkPackage = {
    id: string;
    weight: number;
    ev: number;
    pv: number;
    executorCompanyId?: string | null;
};

export type Company = {
    id: string;
    name: string;
};

type Props = {
    workPackages: WorkPackage[];
    companies: Company[];
    onSelectCompany?: (companyId: string | null) => void;
};

const SPI_THRESHOLD = 0.98;

function clampBar(spi: number) {
    return Math.max(0, Math.min(140, spi * 100));
}

function CompanyRow({
    name,
    spi,
    tone,
    onClick,
}: {
    name: string;
    spi: number;
    tone: 'good' | 'bad';
    onClick?: () => void;
}) {
    const barWidth = clampBar(spi);
    const barClass = tone === 'good' ? 'bg-emerald-500' : 'bg-rose-500';
    const textClass = tone === 'good' ? 'text-emerald-700' : 'text-rose-700';

    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full rounded-xl border px-3 py-2 text-left hover:bg-gray-50"
        >
            <div className="mb-1 flex items-center justify-between gap-3">
                <span className="truncate text-sm font-medium text-gray-800">{name}</span>
                <span className={`text-sm font-semibold tabular-nums ${textClass}`}>SPI {spi.toFixed(2)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full ${barClass}`} style={{ width: `${barWidth}%` }} />
            </div>
        </button>
    );
}

export default function ExecutionSummaryChartCard({ workPackages, companies, onSelectCompany }: Props) {
    const companyNameMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

    const companyStats = useMemo(() => {
        const map = new Map<string, { weight: number; ev: number; pv: number }>();

        for (const wp of workPackages) {
            if (!wp.executorCompanyId) continue;

            const weight = Number.isFinite(wp.weight) ? wp.weight : 0;
            const ev = Number.isFinite(wp.ev) ? wp.ev : 0;
            const pv = Number.isFinite(wp.pv) ? wp.pv : 0;

            const cur = map.get(wp.executorCompanyId) ?? { weight: 0, ev: 0, pv: 0 };
            cur.weight += weight;
            cur.ev += weight * (ev / 100);
            cur.pv += weight * (pv / 100);
            map.set(wp.executorCompanyId, cur);
        }

        return Array.from(map.entries())
            .map(([companyId, v]) => {
                const spi = v.pv > 0 ? v.ev / v.pv : 1;
                return {
                    companyId,
                    name: companyNameMap[companyId] ?? companyId,
                    weight: v.weight,
                    spi,
                };
            })
            .filter((c) => c.weight > 0);
    }, [workPackages, companyNameMap]);

    const goodCompanies = useMemo(() => {
        return [...companyStats]
            .filter((c) => c.spi >= SPI_THRESHOLD)
            .sort((a, b) => b.spi - a.spi)
            .slice(0, 3);
    }, [companyStats]);

    const badCompanies = useMemo(() => {
        return [...companyStats]
            .filter((c) => c.spi < SPI_THRESHOLD)
            .sort((a, b) => a.spi - b.spi)
            .slice(0, 3);
    }, [companyStats]);

    const unassigned = useMemo(() => {
        const totalCount = workPackages.length;
        const list = workPackages.filter((w) => !w.executorCompanyId);
        const count = list.length;

        const totalWeight = workPackages.reduce((s, w) => s + (Number.isFinite(w.weight) ? w.weight : 0), 0);
        const weight = list.reduce((s, w) => s + (Number.isFinite(w.weight) ? w.weight : 0), 0);

        return {
            count,
            totalCount,
            countRatio: totalCount ? count / totalCount : 0,
            weightRatio: totalWeight ? weight / totalWeight : 0,
        };
    }, [workPackages]);

    return (
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-gray-900">Execution Summary</div>
                    <div className="mt-1 text-[11px] text-gray-500">회사별 SPI 상위/주의 그룹</div>
                </div>
                <div className="rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                    기준 SPI 0.98
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="space-y-2">
                    <div className="text-xs font-semibold text-emerald-700">Performing Well · Top 3</div>
                    {goodCompanies.length === 0 && (
                        <div className="rounded-xl border border-dashed px-3 py-4 text-sm text-gray-400">
                            No company
                        </div>
                    )}
                    {goodCompanies.map((c) => (
                        <CompanyRow
                            key={c.companyId}
                            name={c.name}
                            spi={c.spi}
                            tone="good"
                            onClick={() => onSelectCompany?.(c.companyId)}
                        />
                    ))}
                </div>

                <div className="space-y-2">
                    <div className="text-xs font-semibold text-rose-700">Attention Needed · Top 3</div>
                    {badCompanies.length === 0 && (
                        <div className="rounded-xl border border-dashed px-3 py-4 text-sm text-gray-400">
                            No delayed company
                        </div>
                    )}
                    {badCompanies.map((c) => (
                        <CompanyRow
                            key={c.companyId}
                            name={c.name}
                            spi={c.spi}
                            tone="bad"
                            onClick={() => onSelectCompany?.(c.companyId)}
                        />
                    ))}
                </div>
            </div>

            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <div className="text-xs font-semibold text-amber-800">Unassigned WorkPackages</div>
                <div className="mt-1 text-sm text-amber-900">
                    <strong>{unassigned.count}</strong> / {unassigned.totalCount} items
                    <span className="ml-1 text-xs">({(unassigned.countRatio * 100).toFixed(1)}%)</span>
                    <span className="mx-1">·</span>
                    Weight {(unassigned.weightRatio * 100).toFixed(1)}%
                </div>
            </div>
        </div>
    );
}
